import React, { useCallback, useEffect, useMemo, useState } from 'react'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import NavigateBeforeOutlinedIcon from '@mui/icons-material/NavigateBeforeOutlined'
import NavigateNextOutlinedIcon from '@mui/icons-material/NavigateNextOutlined'
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined'
import AddToPhotosOutlinedIcon from '@mui/icons-material/AddToPhotosOutlined'
import { useDispatch } from 'react-redux'
import { getSceneGuestCatalog } from '../../actions/sceneGuestAccess.actions'
import { resolveAnyStaticUrl } from '../../urlConfig'

const getProductImage = (product = {}) => {
    const firstPicture = Array.isArray(product.productPictures)
        ? product.productPictures.find((item) => item?.visibleImg !== false) || product.productPictures[0]
        : null

    return (
        product.reducedImage ||
        firstPicture?.img ||
        firstPicture?.image ||
        firstPicture?.path ||
        firstPicture?.url ||
        firstPicture?.filename ||
        ''
    )
}

const formatDimension = (value) => {
    const number = Number(value)
    return Number.isFinite(number) && number > 0 ? number : null
}

const getDimensionsText = (product) => {
    const values = [
        formatDimension(product?.width),
        formatDimension(product?.height),
        formatDimension(product?.length),
    ].filter((value) => value !== null)

    return values.length ? values.join(' × ') : 'Размер не указан'
}

const formatPrice = (value) => {
    const number = Number(value)
    if (!Number.isFinite(number)) return null

    return new Intl.NumberFormat('ru-RU', {
        maximumFractionDigits: 2,
    }).format(number)
}

const cleanId = (value) => String(value?._id || value?.id || value || '').trim()

const SceneGuestCatalogPanel = ({
    open,
    onClose,
    guestToken,
    enabled,
    pageSize = 8,
    onPreviewProduct,
    previewingProductId = '',
}) => {
    const dispatch = useDispatch()
    const [products, setProducts] = useState([])
    const [categories, setCategories] = useState([])
    const [pagination, setPagination] = useState({
        page: 1,
        total: 0,
        totalPages: 1,
        limit: pageSize,
    })
    const [page, setPage] = useState(1)
    const [categoryId, setCategoryId] = useState('')
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setSearch(searchInput.trim())
            setPage(1)
        }, 320)

        return () => window.clearTimeout(timer)
    }, [searchInput])

    const load = useCallback(async () => {
        if (!open || !enabled || !guestToken) return

        setLoading(true)
        setError('')

        const result = await dispatch(getSceneGuestCatalog(guestToken, {
            page,
            limit: pageSize,
            categoryId: categoryId || undefined,
            search: search || undefined,
        }))

        setLoading(false)

        if (!result?.ok) {
            setError(result?.error || 'Не удалось загрузить каталог')
            return
        }

        setProducts(Array.isArray(result.data?.products) ? result.data.products : [])
        setCategories(Array.isArray(result.data?.categories) ? result.data.categories : [])
        setPagination(result.data?.pagination || {
            page,
            total: 0,
            totalPages: 1,
            limit: pageSize,
        })
    }, [
        categoryId,
        dispatch,
        enabled,
        guestToken,
        open,
        page,
        pageSize,
        search,
    ])

    useEffect(() => {
        load()
    }, [load])

    useEffect(() => {
        if (!open) {
            setSearchInput('')
            setSearch('')
            setCategoryId('')
            setPage(1)
            setError('')
        }
    }, [open])

    const title = useMemo(() => {
        const total = Number(pagination.total || 0)
        return total ? `Каталог офиса · ${total}` : 'Каталог офиса'
    }, [pagination.total])

    if (!open || !enabled) return null

    return (
        <aside className="sceneGuestCatalog" aria-label="Каталог товаров офиса">
            <button
                type="button"
                className="sceneGuestCatalog__backdrop"
                onClick={onClose}
                aria-label="Закрыть каталог"
            />

            <section className="sceneGuestCatalog__panel">
                <header className="sceneGuestCatalog__header">
                    <div>
                        <span>Мини-калькулятор</span>
                        <strong>{title}</strong>
                        <small>
                            Фото показываются в формате 16:9. 3D загружается только после нажатия на конкретный товар.
                        </small>
                    </div>

                    <button type="button" onClick={onClose} aria-label="Закрыть каталог">
                        <CloseOutlinedIcon />
                    </button>
                </header>

                <div className="sceneGuestCatalog__filters">
                    <label className="sceneGuestCatalog__search">
                        <SearchOutlinedIcon />
                        <input
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            placeholder="Название или артикул"
                        />
                    </label>

                    <select
                        value={categoryId}
                        onChange={(event) => {
                            setCategoryId(event.target.value)
                            setPage(1)
                        }}
                    >
                        <option value="">Все разрешённые категории</option>
                        {categories.map((category) => (
                            <option key={cleanId(category)} value={cleanId(category)}>
                                {category.name} ({category.count})
                            </option>
                        ))}
                    </select>
                </div>

                {error ? (
                    <div className="sceneGuestCatalog__status is-error">
                        {error}
                        <button type="button" onClick={load}>Повторить</button>
                    </div>
                ) : null}

                <div className={`sceneGuestCatalog__grid ${loading ? 'is-loading' : ''}`}>
                    {products.map((product) => {
                        const productId = cleanId(product)
                        const image = getProductImage(product)
                        const price = formatPrice(product.price)
                        const previewing = previewingProductId === productId

                        return (
                            <article key={productId} className="sceneGuestCatalog__card">
                                <div className="sceneGuestCatalog__image">
                                    <img
                                        src={resolveAnyStaticUrl(image)}
                                        alt={product.fullName || product.name || 'Товар'}
                                        loading="lazy"
                                    />
                                    {product.has3DModel ? (
                                        <span title="3D-модель доступна по запросу">
                                            <ViewInArOutlinedIcon />
                                            3D
                                        </span>
                                    ) : null}
                                </div>

                                <div className="sceneGuestCatalog__cardBody">
                                    <small>{product.categoryName || 'Без категории'}</small>
                                    <strong title={product.fullName || product.name || 'Товар'}>
                                        {product.fullName || product.name || 'Товар'}
                                    </strong>
                                    <span>
                                        {product.article ? `Арт. ${product.article} · ` : ''}
                                        {getDimensionsText(product)}
                                    </span>

                                    <div className="sceneGuestCatalog__cardFooter">
                                        {price !== null ? <b>{price} ₽</b> : <i>Цена скрыта</i>}

                                        <button
                                            type="button"
                                            disabled={!product.has3DModel || previewing}
                                            onClick={() => onPreviewProduct?.(product)}
                                        >
                                            <AddToPhotosOutlinedIcon />
                                            {previewing
                                                ? 'Загрузка…'
                                                : product.has3DModel
                                                    ? 'Показать в сцене'
                                                    : 'Нет 3D'}
                                        </button>
                                    </div>
                                </div>
                            </article>
                        )
                    })}
                </div>

                {!loading && !products.length && !error ? (
                    <div className="sceneGuestCatalog__empty">
                        По выбранным условиям товаров нет.
                    </div>
                ) : null}

                <footer className="sceneGuestCatalog__pagination">
                    <button
                        type="button"
                        onClick={() => setPage((value) => Math.max(1, value - 1))}
                        disabled={loading || page <= 1}
                    >
                        <NavigateBeforeOutlinedIcon />
                        Назад
                    </button>

                    <span>
                        {pagination.page || page} / {Math.max(1, pagination.totalPages || 1)}
                    </span>

                    <button
                        type="button"
                        onClick={() => setPage((value) => Math.min(pagination.totalPages || value, value + 1))}
                        disabled={loading || page >= Number(pagination.totalPages || 1)}
                    >
                        Дальше
                        <NavigateNextOutlinedIcon />
                    </button>
                </footer>
            </section>
        </aside>
    )
}

export default SceneGuestCatalogPanel
